/** @format */

import React, { useEffect, useState } from "react";
import { FaWalking, FaListAlt } from "react-icons/fa";
import { MdKeyboardArrowDown, MdOutlineFileDownload } from "react-icons/md";
import { saveAs } from "file-saver";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";
import usePermissions from "../../../permissions/permission";
import { motion } from "framer-motion";

const LeaveManagement = () => {
  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const token = Cookies.get("user_token");
  const { hasPermission } = usePermissions();

  // ---------- State ----------
  const [leaveData, setLeaveData] = useState([]);
  const [leaveCategories, setLeaveCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [department, setDepartment] = useState("");
  const [search, setSearch] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const rowsPerPage = 10;

  // ---------- Fetch Leave Categories ----------
  useEffect(() => {
    const fetchLeaveCategories = async () => {
      try {
        const response = await fetch(
          `${API_URL}/v1/hris/leave/getLeaveCategory`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const result = await response.json();
        if (result.success && Array.isArray(result.data)) {
          setLeaveCategories(
            result.data.map((cat) => ({ id: cat.id, name: cat.category_name }))
          );
        }
      } catch (error) {
        console.error("Error fetching leave categories:", error);
      }
    };
    fetchLeaveCategories();
  }, [API_URL, token]);


  useEffect(() => {
    const fetchLeaves = async () => {
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams({
          page: currentPage,
          limit: rowsPerPage,
        });
        if (search) params.append("search", search);
        if (department) params.append("department", department);

        const response = await fetch(
          `${API_URL}/v1/hris/leave/GetLeaveCountstoallemployee?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const result = await response.json();

        if (!response.ok)
          throw new Error(result.error || "Failed to fetch data");

        setLeaveData(result.data || []);
        setTotalPages(Math.ceil(result.totalItems / rowsPerPage));
      } catch (error) {
        setError(error.message);
        setLeaveData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaves();
  }, [API_URL, token, search, department, currentPage]);

  // ---------- Helpers ----------
  const getInitials = (fullName = "", fallback = "??") => {
    if (!fullName) return fallback.slice(0, 2).toUpperCase();
    const parts = String(fullName).trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const handleDepartmentSelect = (dept) => {
    setDepartment(dept === "All Departments" ? "" : dept);
    setIsDropdownOpen(false);
    setCurrentPage(1);
  };

  // ---------- CSV Export ----------
  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (department) params.append("department", department);

      const res = await fetch(
        `${API_URL}/v1/hris/leave/GetLeaveCountstoallemployee?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const result = await res.json();
      const allData = result.data || [];

      if (allData.length === 0) {
        alert("No data available to export.");
        return;
      }

      const headers = ["Employee", ...leaveCategories.map((c) => c.name)];
      const csvRows = [headers.join(",")];

      allData.forEach((emp) => {
        const row = [`"${emp.employee_fullname || "N/A"}"`];
        leaveCategories.forEach((cat) => {
          const current =
            emp.current_leave_counts?.find((x) => x.category_id === cat.id)
              ?.leave_count || 0;
          const actual =
            emp.actual_leave_counts?.find((x) => x.category_id === cat.id)
              ?.leave_count || 0;
          row.push(`"${current}/${actual}"`);
        });
        csvRows.push(row.join(","));
      });

      const blob = new Blob([csvRows.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      saveAs(blob, "Leave_Management_Report.csv");
    } catch (err) {
      console.error("CSV Export Error:", err);
      alert("Error exporting CSV");
    } finally {
      setIsExporting(false);
    }
  };

  // ---------- Render ----------
  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.4 }}
    >
      <div className="mt-5 p-4 font-montserrat">
        <div className="flex space-x-4 mb-5">
          <div className="w-48 h-32 flex flex-col items-center justify-center border rounded-lg shadow hover:shadow-md transition">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mb-2">
              <FaWalking className="text-white" size={20} />
            </div>
            <p className="text-gray-500 text-sm font-bold">Employee Leaves</p>
          </div>

          <Link to="/assign-employee-leave">
            <div className="w-48 h-32 flex flex-col items-center justify-center border rounded-lg shadow hover:shadow-md transition">
              <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2">
                <FaListAlt className="text-gray-500" size={20} />
              </div>

              <p className="text-gray-500 text-sm">Assign Leaves</p>
            </div>
          </Link>
        </div>

        {/* ---------- Filters ---------- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 border rounded-lg shadow bg-gray-50">
          <input
            type="text"
            placeholder="Search employee (No, name, email...)"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full border-gray-300 rounded px-4 py-2"
          />

        </div>

        {/* ---------- Actions ---------- */}
        <div className="flex justify-end mb-3">
          {hasPermission(10003) && (
            <Link to="/view-short-leaves" className="mr-4">
              <button className="px-4 py-2 bg-blue-500 text-white rounded flex items-center gap-2">
                View Short Leave Counts
              </button>
            </Link>
          )}

          <button
            onClick={exportToCSV}
            disabled={isExporting}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
          >
            <MdOutlineFileDownload />
            {isExporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>

        {/* ---------- Table ---------- */}
        <div className="overflow-x-auto bg-white shadow p-2 rounded-xl">
          <table className="w-full border-collapse bg-white text-left text-sm text-gray-600 shadow-lg rounded-lg">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-4 font-medium text-gray-900 sticky left-0 bg-gray-100 z-10">
                  Employee
                </th>
                {leaveCategories.map((category) => (
                  <th key={category.id} className="px-3 py-4 text-center">
                    {category.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 border-t border-gray-200">
              {loading ? (
                <tr>
                  <td
                    colSpan={leaveCategories.length + 1}
                    className="text-center py-4 text-gray-500"
                  >
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan={leaveCategories.length + 1}
                    className="text-center py-4 text-red-500"
                  >
                    {error}
                  </td>
                </tr>
              ) : leaveData.length > 0 ? (
                leaveData.map((emp) => (
                  <tr key={emp.employee_no} className="hover:bg-gray-50">
                    <td className="px-3 py-4 sticky left-0 bg-white z-10">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-blue-400 flex items-center justify-center text-white font-bold">
                          {getInitials(emp.employee_fullname, emp.employee_no)}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {emp.employee_fullname || "N/A"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {emp.employee_no}
                          </div>
                        </div>
                      </div>
                    </td>
                    {leaveCategories.map((cat) => {
                      const current =
                        emp.current_leave_counts?.find(
                          (c) => c.category_id === cat.id
                        )?.leave_count || 0;
                      const actual =
                        emp.actual_leave_counts?.find(
                          (c) => c.category_id === cat.id
                        )?.leave_count || 0;
                      return (
                        <td key={cat.id} className="px-3 py-4 text-center">
                          {current} / {actual}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={leaveCategories.length + 1}
                    className="text-center py-4"
                  >
                    No data found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ---------- Pagination ---------- */}
        <div className="mt-4 flex justify-center items-center space-x-2">
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded disabled:opacity-50"
          >
            Prev
          </button>
          <span>
            Page {currentPage} of {totalPages || 1}
          </span>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0 || loading}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

    </motion.div>

  );
};

export default LeaveManagement;
